import * as React from "react";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./alert-dialog";
import type { ConfirmDialogOptions } from "./confirm-utils";
import { setConfirmDialogImpl, getConfirmDialogImpl } from "./confirm-utils";

export function ConfirmDialogProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<ConfirmDialogOptions | null>(
        null,
    );
    const resolveRef = React.useRef<(value: boolean) => void>();

    const handleOpenChange = React.useCallback((newOpen: boolean) => {
        if (!newOpen) {
            // Dialog is closing, resolve with false if not already resolved
            if (resolveRef.current) {
                resolveRef.current(false);
                resolveRef.current = undefined;
            }
        }
        setOpen(newOpen);
    }, []);

    const handleClose = React.useCallback(() => {
        if (resolveRef.current) {
            resolveRef.current(false);
            resolveRef.current = undefined;
        }
        setOpen(false);
    }, []);

    const handleConfirm = React.useCallback(() => {
        if (resolveRef.current) {
            resolveRef.current(true);
            resolveRef.current = undefined;
        }
        setOpen(false);
    }, []);

    // Cleanup on unmount
    React.useEffect(
        () => () => {
            if (resolveRef.current) {
                resolveRef.current(false);
                resolveRef.current = undefined;
            }
        },
        [],
    );

    const showConfirmDialogImpl = React.useCallback(
        (options: ConfirmDialogOptions) => {
            // Resolve any existing dialog
            if (resolveRef.current) {
                resolveRef.current(false);
                resolveRef.current = undefined;
            }

            setOptions(options);
            setOpen(true);
            return new Promise<boolean>((resolve) => {
                resolveRef.current = resolve;
            });
        },
        [],
    );

    // Set the implementation when the component mounts
    React.useEffect(() => {
        setConfirmDialogImpl(showConfirmDialogImpl);
        return () => {
            // Reset on unmount
            setConfirmDialogImpl(() =>
                Promise.reject(
                    new Error("ConfirmDialogProvider not initialized"),
                ),
            );
        };
    }, [showConfirmDialogImpl]);

    return (
        <>
            {children}
            <AlertDialog open={open} onOpenChange={handleOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{options?.title}</AlertDialogTitle>
                        {options?.description && (
                            <AlertDialogDescription>
                                {options.description}
                            </AlertDialogDescription>
                        )}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleClose}>
                            {options?.cancelText || "Cancel"}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirm}>
                            {options?.confirmText || "Continue"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export const confirmDialog = (options: ConfirmDialogOptions) =>
    getConfirmDialogImpl()(options);
